import React, { useEffect, useMemo, useState } from 'react';
import { SPONSORED_BANNERS_CAROUSEL_EVENT } from 'common/constants';
import { useBreakpoints } from 'common/hooks';
import { BannersCarouselSlider } from './banners-carousel-slider';
import { BannersCarouselItemsList } from './banners-carousel-items-list';
import { BannersCarouselProps } from '../types';
import { enrichContent } from '../utils';
import { proportionalEmptyBanner } from '../styles/banner-carousel-styles';

export const BannersCarousel: React.FC<BannersCarouselProps> = props => {
  const {
    autoplay,
    loop,
    slidesPerView,
    adaptiveImpression,
    onImpressionContentClick,
    onImpressionContentView,
    adaptiveMode = 'strict',
    targetBlank,
  } = props;
  const [mounted, setMounted] = useState(false);
  const { atMobile, atTablet } = useBreakpoints();

  useEffect(() => {
    setMounted(true);
  }, []);

  const { isMobile, isTablet, isDesktop } = useMemo(() => {
    return {
      isDesktop: !atMobile && !atTablet,
      isTablet: atTablet,
      isMobile: atMobile,
    };
  }, [atMobile, atTablet]);

  useEffect(() => {
    if (adaptiveImpression) {
      window.dispatchEvent(
        new CustomEvent<typeof adaptiveImpression>(SPONSORED_BANNERS_CAROUSEL_EVENT, {
          detail: adaptiveImpression,
        })
      );
    }
  }, [adaptiveImpression]);

  if (!mounted) {
    return <div className={proportionalEmptyBanner} />;
  }

  const { desktop, tablet, mobile } = adaptiveImpression;

  const commonProps = {
    onImpressionContentClick,
    onImpressionContentView,
    targetBlank,
  };

  const desktopBanners = desktop ? enrichContent(desktop.content, desktop.contentType) : null;
  let tabletBanners = tablet ? enrichContent(tablet.content, tablet.contentType) : null;
  let mobileBanners = mobile ? enrichContent(mobile.content, mobile.contentType) : null;

  if (adaptiveMode === 'resize') {
    tabletBanners = desktopBanners;
    mobileBanners = desktopBanners;
  }

  if (adaptiveMode === 'no-tablet') {
    tabletBanners = desktopBanners;
  }

  if (adaptiveMode === 'no-mobile') {
    mobileBanners = tabletBanners;
  }

  return (
    <>
      {desktopBanners && isDesktop && (
        <BannersCarouselSlider
          {...{ autoplay, loop, slidesPerView }}
          data-qa="banners-carousel-slider-desktop">
          <BannersCarouselItemsList content={desktopBanners} {...commonProps} />
        </BannersCarouselSlider>
      )}
      {tabletBanners && isTablet && (
        <BannersCarouselSlider
          {...{ autoplay, loop, slidesPerView }}
          data-qa="banners-carousel-slider-tablet">
          <BannersCarouselItemsList content={tabletBanners} {...commonProps} />
        </BannersCarouselSlider>
      )}
      {mobileBanners && isMobile && (
        <BannersCarouselSlider
          {...{ autoplay, loop, slidesPerView: 'auto' }}
          data-qa="banners-carousel-slider-mobile">
          <BannersCarouselItemsList content={mobileBanners} {...commonProps} />
        </BannersCarouselSlider>
      )}
    </>
  );
};
